import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, LogOut, Loader2, Plus, Trash2, GripVertical, ExternalLink, Instagram, Eye, EyeOff, Upload, Image, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import AdminSidebar from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSocial() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    image_url: "",
    caption: "",
    instagram_link: "",
    is_active: true
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    verifyAndFetch(token);
  }, [navigate]);

  const verifyAndFetch = async (token) => {
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      fetchPosts();
    } catch (error) {
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/social-posts`, getAuthHeaders());
      setPosts(response.data);
    } catch (error) {
      toast.error("Error loading posts");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        image_url: post.image_url,
        caption: post.caption || "",
        instagram_link: post.instagram_link || "",
        is_active: post.is_active
      });
    } else {
      setEditingPost(null);
      setFormData({ image_url: "", caption: "", instagram_link: "", is_active: true });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPost(null);
    setFormData({ image_url: "", caption: "", instagram_link: "", is_active: true });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, or WebP)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    try {
      setUploading(true);
      const uploadData = new FormData();
      uploadData.append('file', file);
      
      const response = await axios.post(`${API}/upload/social-image`, uploadData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData(p => ({ ...p, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      toast.error('Please upload or enter an image URL');
      return;
    }
    
    try {
      setSubmitting(true);
      if (editingPost) {
        await axios.put(`${API}/social-posts/${editingPost.id}`, formData, getAuthHeaders());
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/social-posts`, formData, getAuthHeaders());
        toast.success('Post created successfully');
      }
      handleCloseDialog();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/social-posts/${postId}`, getAuthHeaders());
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Error deleting post');
    }
  };

  const handleToggleActive = async (post) => {
    try {
      await axios.put(`${API}/social-posts/${post.id}`, { is_active: !post.is_active }, getAuthHeaders());
      fetchPosts();
    } catch (error) {
      toast.error('Error updating post');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newPosts = [...posts];
    const draggedPost = newPosts[draggedItem];
    newPosts.splice(draggedItem, 1);
    newPosts.splice(index, 0, draggedPost);
    
    setPosts(newPosts);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;
    
    try {
      const order = (posts || []).map(p => p.id);
      await axios.post(`${API}/social-posts/reorder`, order, getAuthHeaders());
    } catch (error) {
      toast.error('Error reordering posts');
      fetchPosts();
    }
    setDraggedItem(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex">
      {/* Sidebar */}
      <AdminSidebar />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-5xl">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-2xl text-[#676664] font-light mb-2">Social Feed</h1>
              <p className="text-sm text-[#676664]/60">Manage Instagram-style posts for your homepage carousel</p>
            </div>
            <Button onClick={() => handleOpenDialog()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="add-post-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Post
            </Button>
          </div>

          {(posts || []).length === 0 ? (
            <div className="bg-white border border-[#282828]/10 p-12 text-center">
              <Instagram className="w-12 h-12 text-[#9A7B4F]/30 mx-auto mb-4" strokeWidth={1} />
              <h3 className="text-lg text-[#676664] mb-2">No posts yet</h3>
              <p className="text-sm text-[#676664]/50 mb-6">Add your first Instagram-style post to display on the homepage</p>
              <Button onClick={() => handleOpenDialog()} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white">
                <Plus className="w-4 h-4 mr-2" /> Add First Post
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(posts || []).map((post, index) => (
                <div
                  key={post.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`bg-white border border-[#282828]/10 overflow-hidden group relative cursor-move ${
                    !post.is_active ? 'opacity-50' : ''
                  } ${draggedItem === index ? 'ring-2 ring-[#9A7B4F]' : ''}`}
                >
                  {/* Drag handle */}
                  <div className="absolute top-2 left-2 z-10 bg-white/90 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="w-4 h-4 text-[#676664]/40" />
                  </div>
                  
                  {/* Status badge */}
                  <div className={`absolute top-2 right-2 z-10 px-2 py-0.5 text-[10px] font-medium rounded ${
                    post.is_active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
                  }`}>
                    {post.is_active ? 'Active' : 'Hidden'}
                  </div>
                  
                  {/* Image */}
                  <div className="aspect-square overflow-hidden bg-[#FAF8F0]">
                    <img 
                      src={post.image_url} 
                      alt={post.caption || 'Social post'} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Actions */}
                  <div className="p-3 border-t border-[#282828]/10">
                    {post.caption && (
                      <p className="text-[11px] text-[#676664]/60 mb-2 line-clamp-2">{post.caption}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleOpenDialog(post)}
                          className="p-1.5 text-[#676664]/40 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/10 transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleToggleActive(post)}
                          className="p-1.5 text-[#676664]/40 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/10 transition-colors"
                          title={post.is_active ? 'Hide' : 'Show'}
                        >
                          {post.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        {post.instagram_link && (
                          <a
                            href={post.instagram_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-[#676664]/40 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/10 transition-colors"
                            title="View on Instagram"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-[11px] text-[#676664]/40 mt-6">
            Tip: Drag posts to reorder. Only active posts will appear on the homepage (max 9).
          </p>
        </div>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#676664]">{editingPost ? 'Edit Post' : 'Add New Post'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Image Upload */}
            <div>
              <Label className="text-[#676664]/60 text-[11px]">Image *</Label>
              <div className="mt-2 space-y-3">
                {/* Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed border-[#282828]/20 rounded-lg p-6 text-center cursor-pointer hover:border-[#9A7B4F]/50 transition-colors ${
                    formData.image_url ? 'border-[#9A7B4F]' : ''
                  }`}
                >
                  {formData.image_url ? (
                    <div className="relative aspect-square max-w-[200px] mx-auto">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-full object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFormData(p => ({ ...p, image_url: '' })); }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ) : uploading ? (
                    <div className="py-4">
                      <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F] mx-auto mb-2" />
                      <p className="text-sm text-[#676664]/60">Uploading...</p>
                    </div>
                  ) : (
                    <div className="py-4">
                      <Upload className="w-8 h-8 text-[#9A7B4F]/50 mx-auto mb-2" />
                      <p className="text-sm text-[#676664]/60">Click to upload image</p>
                      <p className="text-[10px] text-[#676664]/40 mt-1">JPEG, PNG, WebP • Max 5MB • Square recommended</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                />
                
                {/* Or paste URL */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-[#6B5B4F]/10"></div>
                  <span className="text-[10px] text-[#676664]/40">or paste URL</span>
                  <div className="flex-1 h-px bg-[#6B5B4F]/10"></div>
                </div>
                <Input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData(p => ({ ...p, image_url: e.target.value }))}
                  placeholder="https://..."
                  className="border-[#282828]/10"
                />
              </div>
            </div>

            {/* Caption */}
            <div>
              <Label className="text-[#676664]/60 text-[11px]">Caption (optional)</Label>
              <Textarea
                value={formData.caption}
                onChange={(e) => setFormData(p => ({ ...p, caption: e.target.value }))}
                placeholder="Write a caption..."
                className="mt-2 border-[#282828]/10 resize-none"
                rows={3}
                maxLength={300}
              />
              <p className="text-[10px] text-[#676664]/40 mt-1">{formData.caption.length}/300 characters</p>
            </div>

            {/* Instagram Link */}
            <div>
              <Label className="text-[#676664]/60 text-[11px]">Instagram Post Link (optional)</Label>
              <Input
                type="url"
                value={formData.instagram_link}
                onChange={(e) => setFormData(p => ({ ...p, instagram_link: e.target.value }))}
                placeholder="https://instagram.com/p/..."
                className="mt-2 border-[#282828]/10"
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between py-3 border-y border-[#282828]/10">
              <div>
                <Label className="text-[#676664] text-sm">Show on homepage</Label>
                <p className="text-[10px] text-[#676664]/50">Post will appear in the Instagram carousel</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(p => ({ ...p, is_active: checked }))}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 border-[#282828]/10">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !formData.image_url} className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingPost ? 'Update' : 'Add Post'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
